import { useEffect } from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Image,
  Link,
  Stack,
  Text
} from '@chakra-ui/react'
import { ImagesPng } from 'assets/images'
import { SingleButton } from 'common/Buttons'
import { LoadingSpinier } from 'common/Loading'
import dayjs from 'dayjs'
import {
  acceptFriend,
  archiveRequest,
  denyFriend,
  removeRemovedFriend,
  setAcceptFriendStatus,
  setArchiveRequestStatus,
  setDenyFriendStatus,
  setNotificationSectionPage,
  setProfileItemId,
  setRemoveRemovedFriendStatus,
  showCurrentChatFriendMarkerCoordinate,
  usersSelectors
} from 'store'
import {
  NOTIFICATION_TYPE,
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { useGoToProfileMarker } from 'hooks/useGoToProfileMarker'
import { statusMarkerColor } from 'utils/helpers'
import { COLORS } from 'utils/styles'

import {
  StyledFlexContainer,
  StyledWordsContent
} from './styled'

export const RequestDetails = () => {
  const goToProfileMarker = useGoToProfileMarker()
  const {
    acceptFriendStatus,
    activeProfile,
    archiveRequestStatus,
    connReqData,
    denyFriendStatus,
    getProfileItemStatus,
    profileItem,
    removeRemovedFriendStatus
  } = useSelector(usersSelectors)
  const dispatch = useDispatch()
  const validDate = dayjs(profileItem?.last_accessed)
  const statusColor = statusMarkerColor(validDate)

  const ongoingAPIcall = acceptFriendStatus === PENDING
    || archiveRequestStatus === PENDING
    || denyFriendStatus === PENDING

  const btnCustomStyle = {
    alignItems: 'center',
    height: '30px',
    padding: '10px',
    pointerEvents: ongoingAPIcall ? 'none' : 'auto',
    cursor: ongoingAPIcall ? 'not-allowed' : 'pointer',
    background: ongoingAPIcall ? COLORS.gray : COLORS.gold
  }

  const handleNotificationButton = ({
    id,
    type
  }) => {
    if (type === NOTIFICATION_TYPE.Accept) {
      dispatch(acceptFriend({
        id,
        activeId: activeProfile?.id
      }))
    } else if (type === NOTIFICATION_TYPE.Unblock) {
      dispatch(removeRemovedFriend({
        profile_id: id,
        activeId: activeProfile?.id
      }))
    } else {
      dispatch(denyFriend({
        id,
        activeId: activeProfile?.id
      }))
    }
  }

  useEffect(() => {
    if (acceptFriendStatus === SUCCEEDED) {
      dispatch(setNotificationSectionPage(null))
      dispatch(setAcceptFriendStatus(0))
    }
  }, [acceptFriendStatus])

  useEffect(() => {
    if (archiveRequestStatus === SUCCEEDED) {
      dispatch(setNotificationSectionPage(null))
      dispatch(setArchiveRequestStatus(0))
    }
  }, [archiveRequestStatus])

  useEffect(() => {
    if (denyFriendStatus === SUCCEEDED) {
      dispatch(setNotificationSectionPage(null))
      dispatch(setDenyFriendStatus(0))
    }
  }, [denyFriendStatus])

  useEffect(() => {
    if (removeRemovedFriendStatus === SUCCEEDED) {
      dispatch(setNotificationSectionPage(null))
      dispatch(setRemoveRemovedFriendStatus(0))
    }
  }, [removeRemovedFriendStatus])

  return getProfileItemStatus === REJECTED
    ? (
      <Box width="100%" height="170px">
        <LoadingSpinier />
      </Box>
    ) : (
      <>
        <Stack
          width="100%"
          position="relative"
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing="16px"
        >
          <Box
            backgroundImage={profileItem?.banner || ImagesPng?.DefaultImage}
            backgroundColor={null || 'gray.700'}
            bgPosition="100%"
            bgRepeat="no-repeat"
            bgSize="452px 110px"
            position="absolute"
            width="100%"
            height="110px"
            padding="20px"
            top="0px"
            left={0}
            data-name="coverPicture"
            sx={{
              '@media (max-width: 470px)': {
                width: '100%'
              }
            }}
          >
            <Box />
          </Box>

          <Grid
            width="100%"
            height="95px"
            templateRows="repeat(4, 5fr)"
            templateColumns="repeat(6, 1fr)"
          >
            <GridItem
              rowStart={1}
              rowSpan={2}
              colSpan={6}
            >
              <Stack
                alignItems="center"
                direction="row"
                position="relative"
                padding="20px"
                height="105px"
                marginTop="1px"
                sx={{
                  '@media (max-width: 480px)': {
                    left: '10px !important'
                  }
                }}
              >
                <Image
                  boxSize="80px"
                  position="relative"
                  objectFit="cover"
                  src={profileItem?.profile_picture || ImagesPng.DefaultImage}
                  alt="User Image"
                  borderRadius="full"
                  cursor="pointer"
                  border={`1.5px solid ${COLORS.gray_op8}`}
                  onClick={() => {
                    goToProfileMarker({
                      profileId: connReqData?.sender_id,
                      coordinates: connReqData?.coordinates
                    })
                  }}
                />

                <Text
                  backgroundColor={COLORS.transparentGray}
                  p="1.5px 15px"
                  borderRadius={3}
                  data-name="fullName"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  fontSize="17px"
                >
                  {profileItem?.full_name}
                </Text>
              </Stack>
            </GridItem>

            <Flex
              gap="10px"
              paddingTop="5px"
              paddingRight="10px"
              position="absolute"
              right="0"
            >
              <SingleButton
                text={
                  connReqData?.type === 'removed_friend'
                    ? 'Unblock'
                    : 'Block'
                }
                customStyled={{
                  ...btnCustomStyle,
                  display: connReqData?.type === 'denied' ? 'none' : 'auto'
                }}
                isLoading={
                  (
                    denyFriendStatus === PENDING
                    || removeRemovedFriendStatus === PENDING
                  )
                  && profileItem?.id === connReqData?.profile_id
                }
                click={() => {
                  handleNotificationButton({
                    id: connReqData?.type === 'removed_friend'
                      ? profileItem?.id
                      : sessionStorage.getItem('id'),
                    type: connReqData?.type === 'removed_friend'
                      ? NOTIFICATION_TYPE.Unblock
                      : NOTIFICATION_TYPE.Reject
                  })
                  dispatch(setProfileItemId(null))
                }}
              />
              <SingleButton
                text="Archive"
                customStyled={{
                  ...btnCustomStyle,
                  display: connReqData?.type === 'removed_friend' ? 'none' : 'auto'
                }}
                isLoading={
                  archiveRequestStatus === PENDING
                  && profileItem?.id === connReqData?.profile_id
                }
                click={() => {
                  dispatch(archiveRequest({
                    id: localStorage.getItem('friendReqID'),
                    activeID: activeProfile?.id
                  }))
                  dispatch(setProfileItemId(null))
                }}
              />

              <SingleButton
                text="Accept"
                customStyled={{
                  ...btnCustomStyle,
                  display: connReqData?.type === 'removed_friend' ? 'none' : 'auto'
                }}
                isLoading={
                  acceptFriendStatus === PENDING
                  && profileItem?.id === connReqData?.profile_id
                }
                click={() => {
                  handleNotificationButton({
                    id: sessionStorage.getItem('id'),
                    type: NOTIFICATION_TYPE.Accept
                  })
                  dispatch(setProfileItemId(null))
                }}
              />
            </Flex>
            <Box />
          </Grid>
        </Stack>

        <Stack
          direction="column"
          spacing="16px"
          sx={{ marginTop: '30px !important' }}
        >
          <Text
            align="center"
            data-name="quote"
          >
            {profileItem?.personal_quote}
          </Text>
        </Stack>

        {/* Connections */}
        <Stack
          textAlign="center"
          direction="column"
          justifyContent="space-between"
          padding="0 50px"
        >
          <Stack
            justifyContent="space-between"
            direction="row"
            spacing="10px"
          >
            <Text color={COLORS.darkGray}>Connections</Text>
            <Text>
              {profileItem?.active_connections}
            </Text>
          </Stack>

          <Stack
            justifyContent="space-between"
            direction="row"
            spacing="10px"
          >
            <Text color={COLORS.darkGray}>Disconnections</Text>
            <Text>
              {profileItem?.past_connections}
              %
            </Text>
          </Stack>

          <Stack
            justifyContent="space-between"
            direction="row"
            spacing="10px"
          >
            <Text color={COLORS.darkGray}>Status</Text>
            <Text>
              {statusColor}
            </Text>
          </Stack>
        </Stack>

        {/* Affinities */}
        <Stack
          padding={5}
          spacing="16px"
        >
          <Text
            fontSize="20px"
            fontWeight={700}
          >
            Affinities
          </Text>
          <StyledFlexContainer>
            {
              profileItem?.affinities?.map((aff) => (
                <StyledWordsContent>{aff.title}</StyledWordsContent>
              ))
            }
          </StyledFlexContainer>
        </Stack>

        {/* Social Media */}
        <Stack padding={5}>
          <Text fontSize="20px" fontWeight={700}>
            Social Media
          </Text>

          <Stack
            direction="column"
            marginX={5}
          >
            <Stack direction="column">
              {
                profileItem?.social_links?.length > 0
                  ? (
                    <Stack
                      direction="row"
                      spacing="16px"
                    >
                      <Stack
                        gap="10px"
                        spacing="unset"
                        flexWrap="wrap"
                        direction="row"
                      >
                        {
                          profileItem.social_links.map((item) => (
                            <Box
                              borderRadius="full"
                              bg={COLORS.gold}
                              p="6px 10px"
                            >
                              <Link
                                href={item.link}
                                target="_blank"
                                name={item.name}
                                _cursor="pointer"
                                style={{
                                  textDecoration: 'none',
                                  color: 'black'
                                }}
                              >
                                {item.name}
                              </Link>
                            </Box>
                          ))
                        }
                      </Stack>
                    </Stack>
                  ) : <Text as="span" color={COLORS.gray}>No data available</Text>
              }
            </Stack>
          </Stack>
        </Stack>
      </>
    )
}
